import { gql } from '@apollo/client';

/**
 * GraphQL query for fetching account summary data
 * This query fetches all fields needed for the Summary tab
 */
export const GET_ACCOUNT_SUMMARY = gql`
  query GetAccountSummary($id: ID!) {
    node(id: $id) {
      ... on AccountNode {
        id

        # Account Information
        name
        directInvesting
        isLpPlatform
        smPortfolioFund
        smFundLender
        smServiceProvider
        email
        mainPhone
        preferredEmailAddress
        phone
        website

        primaryContactId {
          id
          name
          email
        }

        # Texts
        description
        nextSteps
        lastCompletedStep

        # Primary Address
        primaryAddressIds {
          id
          address1Street1
          address1Street2
          city
          stateProvince
          zipPostalCode
          countryRegion
          address1
        }

        # Relationship Strengths
        relationshipType
        fullRelationshipType
        relationshipPriority
        suggestedRelationshipType
        miscategorizedRelationshipType
        miscategorizedRelationshipTypeRequestDate
        miscategorizedRelationshipTypeUpdateDate
        division
        # Social Media
        socialMediaIds {
          id
          linkedin
          facebook
          instagram
          twitter
          youtube
        }

        # Others
        alternateAccountName1
        alternateAccountName2
        division
        parentAccountId {
          id
          name
        }
        mlIsPbig
        yearFounded
        ownerId {
          id
          name
        }
        secondaryOwnerId {
          id
          name
        }
        investorCategory
        smPortfolioCompany
        fundType
        placementAgent
        lpPlatformSuppressEmails
        convergency
        muranoDateRecieved
        isMuranoApproved
        muranoTrackings {
          id
          muranoStatus
          muranoTeam
          muranoApproved
          muranoDateReceived
          muranoIo
          muranoNotes
        }
      }
    }
  }
`;

export interface AccountNode {
  alternateAccountName1?: null | string;
  alternateAccountName2?: null | string;
  convergency?: boolean | null;
  description?: null | string;
  directInvesting?: boolean | null;
  division?: null | string;
  email?: null | string;
  fullRelationshipType?: null | string;
  fundType?: null | string;
  id: string;
  isLpPlatform?: boolean | null;
  isMuranoApproved?: boolean | null;
  lastCompletedStep?: null | string;
  lpPlatformSuppressEmails?: boolean | null;
  mainPhone?: null | string;
  miscategorizedRelationshipType?: boolean | null;
  miscategorizedRelationshipTypeRequestDate?: null | string;
  miscategorizedRelationshipTypeUpdateDate?: null | string;
  mlIsPbig?: boolean | null;
  muranoDateRecieved?: null | string;
  muranoTrackings?:
    | AccountNodeMuranoTracking
    | AccountNodeMuranoTracking[]
    | null;
  name: string;
  nextSteps?: null | string;
  parentAccountId?: AccountNodeParent | null;
  phone?: null | string;
  placementAgent?: null | string;
  preferredEmailAddress?: null | string;
  primaryAddressIds?: AccountNodeAddress | AccountNodeAddress[] | null;
  primaryContactId?: AccountNodePrimaryContact | null;
  relationshipPriority?: null | string;
  relationshipType?: null | string;
  smFundLender?: boolean | null;
  smPortfolioCompany?: null | string;
  smPortfolioFund?: boolean | null;
  smServiceProvider?: boolean | null;
  socialMediaIds?: AccountNodeSocialMedia | AccountNodeSocialMedia[] | null;
  suggestedRelationshipType?: null | string;
  website?: null | string;
  yearFounded?: null | number;
}

export interface AccountNodeAddress {
  address1?: null | string;
  address1Street1?: null | string;
  address1Street2?: null | string;
  city?: null | string;
  countryRegion?: null | string;
  id: string;
  stateProvince?: null | string;
  zipPostalCode?: null | string;
}

export interface AccountNodeMuranoTracking {
  id: string;
  muranoApproved?: boolean | null;
  muranoDateReceived?: null | string;
  muranoIo?: boolean | null;
  muranoNotes?: null | string;
  muranoStatus: string;
  muranoTeam: string;
}

export interface AccountNodeParent {
  id: string;
  name: null | string;
}

export interface AccountNodePrimaryContact {
  email?: null | string;
  id: string;
  name: string;
}

export interface AccountNodeSocialMedia {
  facebook?: null | string;
  id: string;
  instagram?: null | string;
  linkedin?: null | string;
  twitter?: null | string;
  youtube?: null | string;
}

export interface AccountSummaryData {
  accountName: string;
  address1: string;
  address1Street1: string;
  address1Street2: string;
  alternativeAccountName1: string;
  alternativeAccountName2: string;
  city: string;
  complementedStep: string;
  convergencyCollaboration: boolean;
  countryRegion: string;
  description: string;
  directInvesting: boolean;
  email: string;
  facebook: string;
  fullRelationshipType: string;
  fundType: string;
  instagram: string;
  linkedin: string;
  lpPlatform: boolean;
  lpPlatformSuppressEmails: boolean;
  mainPhone: string;
  miscategorizedRelationshipType: boolean;
  miscategorizedRelationshipTypeRequestDate: string;
  miscategorizedRelationshipTypeUpdateDate: string;
  mlDivision: string;
  mlIsPbig: boolean;
  muranoApproved: boolean;
  muranoDateReceived: string;
  muranoIo: boolean;
  muranoNotes: string;
  muranoStatus: string;
  muranoTeam: string;
  name: string;
  nextSteps: string;
  parentAccount: string;
  parentAccountId: string;
  phone: string;
  preferredEmailAddress: string;
  primaryContact: string;
  primaryContactId: string;
  relationshipPriority: string;
  relationshipType: string;
  starMountainFundLender: boolean;
  starMountainPlacementAgent: string;
  starMountainPortfolioCompany: string;
  starMountainPortfolioFund: boolean;
  starMountainServiceProvider: boolean;
  stateProvince: string;
  suggestedRelationshipType: string;
  twitter: string;
  website: string;
  yearFounded: string;
  youtube: string;
  zipPostalCode: string;
}

export interface GetAccountSummaryData {
  node: AccountNode | null;
}

export interface GetAccountSummaryVariables {
  id: string;
}

export function createEmptyAccountSummaryData(): AccountSummaryData {
  return {
    accountName: '',
    address1: '',
    address1Street1: '',
    address1Street2: '',
    alternativeAccountName1: '',
    alternativeAccountName2: '',
    city: '',
    complementedStep: '',
    convergencyCollaboration: false,
    countryRegion: '',
    description: '',
    directInvesting: false,
    email: '',
    facebook: '',
    fullRelationshipType: '',
    fundType: '',
    instagram: '',
    linkedin: '',
    lpPlatform: false,
    lpPlatformSuppressEmails: false,
    mainPhone: '',
    miscategorizedRelationshipType: false,
    miscategorizedRelationshipTypeRequestDate: '',
    miscategorizedRelationshipTypeUpdateDate: '',
    mlDivision: '',
    mlIsPbig: false,
    muranoApproved: false,
    muranoDateReceived: '',
    muranoIo: false,
    muranoNotes: '',
    muranoStatus: '',
    muranoTeam: '',
    name: '',
    nextSteps: '',
    parentAccount: '',
    parentAccountId: '',
    phone: '',
    preferredEmailAddress: '',
    primaryContact: '',
    primaryContactId: '',
    relationshipPriority: '',
    relationshipType: '',
    starMountainFundLender: false,
    starMountainPlacementAgent: '',
    starMountainPortfolioCompany: '',
    starMountainPortfolioFund: false,
    starMountainServiceProvider: false,
    stateProvince: '',
    suggestedRelationshipType: '',
    twitter: '',
    website: '',
    yearFounded: '',
    youtube: '',
    zipPostalCode: ''
  };
}

export function mapAccountSummary(node: AccountNode): AccountSummaryData {
  // Handle both array and single object cases
  const socialMedia = Array.isArray(node.socialMediaIds)
    ? (node.socialMediaIds[0] ?? null)
    : (node.socialMediaIds ?? null);
  const primaryAddress = Array.isArray(node.primaryAddressIds)
    ? (node.primaryAddressIds[0] ?? null)
    : (node.primaryAddressIds ?? null);
  const muranoTracking = Array.isArray(node.muranoTrackings)
    ? (node.muranoTrackings[0] ?? null)
    : (node.muranoTrackings ?? null);

  return {
    accountName: node.name,
    address1: primaryAddress?.address1 ?? '',
    address1Street1: primaryAddress?.address1Street1 ?? '',
    address1Street2: primaryAddress?.address1Street2 ?? '',
    alternativeAccountName1: node.alternateAccountName1 ?? '',
    alternativeAccountName2: node.alternateAccountName2 ?? '',
    city: primaryAddress?.city ?? '',
    complementedStep: node.lastCompletedStep ?? '',
    convergencyCollaboration: node.convergency ?? false,
    countryRegion: primaryAddress?.countryRegion ?? '',
    description: node.description ?? '',
    directInvesting: node.directInvesting ?? false,
    email: node.email ?? '',
    facebook: socialMedia?.facebook ?? '',
    fullRelationshipType: node.fullRelationshipType ?? '',
    fundType: node.fundType ?? '',
    instagram: socialMedia?.instagram ?? '',
    linkedin: socialMedia?.linkedin ?? '',
    lpPlatform: node.isLpPlatform ?? false,
    lpPlatformSuppressEmails: node.lpPlatformSuppressEmails ?? false,
    mainPhone: node.mainPhone ?? '',
    miscategorizedRelationshipType:
      node.miscategorizedRelationshipType ?? false,
    miscategorizedRelationshipTypeRequestDate:
      node.miscategorizedRelationshipTypeRequestDate ?? '',
    miscategorizedRelationshipTypeUpdateDate:
      node.miscategorizedRelationshipTypeUpdateDate ?? '',
    mlDivision: node.division ?? '',
    mlIsPbig: node.mlIsPbig ?? false,
    muranoApproved:
      muranoTracking?.muranoApproved ?? node.isMuranoApproved ?? false,
    muranoDateReceived:
      muranoTracking?.muranoDateReceived ?? node.muranoDateRecieved ?? '',
    muranoIo: muranoTracking?.muranoIo ?? false,
    muranoNotes: muranoTracking?.muranoNotes ?? '',
    muranoStatus: muranoTracking?.muranoStatus ?? '',
    muranoTeam: muranoTracking?.muranoTeam ?? '',
    name: node.name,
    nextSteps: node.nextSteps ?? '',
    parentAccount: node.parentAccountId?.name ?? '',
    parentAccountId: node.parentAccountId?.id ?? '',
    phone: node.phone ?? '',
    preferredEmailAddress: node.preferredEmailAddress ?? '',
    primaryContact: node.primaryContactId?.name ?? '',
    primaryContactId: node.primaryContactId?.id ?? '',
    relationshipPriority: node.relationshipPriority ?? '',
    relationshipType: node.relationshipType ?? '',
    starMountainFundLender: node.smFundLender ?? false,
    starMountainPlacementAgent: node.placementAgent ?? '',
    starMountainPortfolioCompany: node.smPortfolioCompany ?? '',
    starMountainPortfolioFund: node.smPortfolioFund ?? false,
    starMountainServiceProvider: node.smServiceProvider ?? false,
    stateProvince: primaryAddress?.stateProvince ?? '',
    suggestedRelationshipType: node.suggestedRelationshipType ?? '',
    twitter: socialMedia?.twitter ?? '',
    website: node.website ?? '',
    yearFounded: node.yearFounded != null ? String(node.yearFounded) : '',
    youtube: socialMedia?.youtube ?? '',
    zipPostalCode: primaryAddress?.zipPostalCode ?? ''
  };
}
